import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useOutletContext } from 'react-router-dom';

import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { Section } from '~/components';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { CardToken, CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

import { CardProps } from './Card';

const columns: TableColumn<CardToken>[] = [
  {
    header: 'Wallet Type',
    accessor: 'walletType',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value) : '-'} />,
  },
  {
    header: 'Token Type',
    accessor: 'tokenType',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
  },
  {
    header: 'Device Name',
    accessor: 'deviceName',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={(value as string) || '-'} />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value) : '-'} />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => (value ? <RelativeTime timestamp={value as Date} /> : <Table.TextCell value="-" />),
  },
];

export const PageCardTokens: React.FC = () => {
  const navigate = useNavigate();
  const { card } = useOutletContext<CardProps>();
  const { currentPlatform, isSandbox } = useSessionStore();

  const { data: tokensData, isLoading } = useQuery({
    queryKey: ['card-tokens', card?.id, currentPlatform?.id, isSandbox],
    queryFn: () => CardTokenRepository.listCardTokens({ cardId: card.id }),
    enabled: !!card?.id,
  });

  return (
    <Inner p={0}>
      <Section headline="Tokens">
        <Inner px="var(--page-margin)" py={0}>
          <Table<CardToken>
            columns={columns}
            data={tokensData?.cardTokens ?? []}
            isLoading={isLoading}
            variant={TableVariant.List}
            onRowClick={(row) => {
              navigate(`${ROUTE.CARD_TOKENS}/edit/${row.id}`);
            }}
          />
        </Inner>
      </Section>
    </Inner>
  );
};
